import type { AxiosResponse } from 'axios';
import type { ApiPlugin } from '@srm/core/http';
import { sendNotification } from '@srm/ui/lib/notifications';
import { readHeader, TRACE_ID_HEADER } from '@srm/core/errors';

/**
 * Бэк не смог проверить правила видимости части действий (ошибка конфигурации: правило
 * ссылается на реквизит, которого нет у этого типа документа). Такие действия он скрыл,
 * остальные вернул, а названия скрытых перечислил в этом заголовке.
 */
export const ACTION_LOAD_ERRORS_HEADER = 'x-action-load-errors';

export interface ActionLoadErrors {
    /** Названия скрытых действий; бэк обрезает список, полное число — в `total`. */
    names: string[];
    total: number;
}

/** Значение — percent-encoded JSON `{"names":[...],"total":N}`. Мусор молча игнорируем. */
export const parseActionLoadErrors = (raw: string | undefined): ActionLoadErrors | undefined => {
    if (!raw) return undefined;

    let parsed: unknown;
    try {
        parsed = JSON.parse(decodeURIComponent(raw));
    } catch {
        return undefined;
    }

    const { names, total } = (parsed ?? {}) as { names?: unknown; total?: unknown };
    const valid = Array.isArray(names)
        ? names.filter((name): name is string => typeof name === 'string' && name.trim() !== '')
        : [];
    if (valid.length === 0) return undefined;

    return {
        names: valid,
        total: typeof total === 'number' && Number.isInteger(total) && total > valid.length ? total : valid.length,
    };
};

export const formatActionLoadErrors = ({ names, total }: ActionLoadErrors): string => {
    const list = names.map((name) => `«${name}»`).join(', ');
    const rest = total - names.length;

    return total === 1
        ? `Не удалось проверить условия действия ${list}, поэтому оно скрыто. Сообщите администратору.`
        : `Не удалось проверить условия действий ${list}${rest > 0 ? ` и ещё ${rest}` : ''}, ` +
              'поэтому они скрыты. Сообщите администратору.';
};

const escapeHtml = (text: string) =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Тост о скрытых действиях — на всех стендах, включая PROD: в отличие от generic-тоста
 * об отказе запроса, здесь пользователь иначе не узнает, почему пропала кнопка.
 */
export const notifyActionLoadErrors = (response: AxiosResponse | undefined) => {
    if (!response || response.config?.suppressErrorNotification) return;

    const errors = parseActionLoadErrors(readHeader(response.headers, ACTION_LOAD_ERRORS_HEADER));
    if (!errors) return;

    sendNotification({
        // Один id на один набор: список действий перезапрашивается на каждый клик по строке
        // и на каждое обновление карточки, а Mantine не показывает второй тост с тем же id.
        id: `action-load-errors:${errors.names.join('|')}`,
        title: 'Часть действий недоступна',
        // Текст уходит в innerHTML через DOMPurify — названия из конфигурации экранируем.
        message: escapeHtml(formatActionLoadErrors(errors)),
        variant: 'yellow',
        traceId: readHeader(response.headers, TRACE_ID_HEADER),
    });
};

/** Плагин клиента: тост о скрытых действиях — по успешному ответу. */
export const actionLoadErrorsPlugin = (): ApiPlugin => ({
    name: 'action-load-errors',
    response: (response, outcome) => {
        if (outcome === 'success') notifyActionLoadErrors(response);
    },
});
